/**
 * Per-step gating for the Add-property wizard.
 *
 * Pure and dependency-free (no supabase client, no components) so the rules —
 * notably the street-name rule, which keeps abbreviations off the contract and
 * the Wohnungsgeberbestätigung — can be unit-tested on their own.
 */
import { validateStreetName } from "@/lib/address";
import { bedroomsOf, computeChecklistProgress } from "./checklist";
import type { WizardState } from "./wizardContext";

/** Identity fallback so callers (and this file's own tests) can omit `t`. */
const IDENTITY_T = (_key: string, fallback?: string) => fallback ?? _key;

/**
 * Whether the wizard may leave `step`. Exported for its own tests — the
 * street-name rule in step 1 is the kind of thing that quietly stops being
 * enforced when the field list is edited.
 *
 * `t` defaults to a passthrough (English fallback text) so tests can call
 * this without wiring up LanguageContext; the real caller (PropertyNew.tsx)
 * passes useLanguage()'s `t`.
 */
export function validateWizardStep(
  state: WizardState,
  step: number,
  t: (key: string, fallback?: string) => string = IDENTITY_T,
  opts: {
    /** Representor fields are hidden entirely for an owner submitting their
     *  own building (Step2Apartments) — there's nothing here to require. */
    ownerSubmitting?: boolean;
    /**
     * `rep_name` (trimmed) by lessor id, for every lessor a linked apartment
     * could point at. A non-null `lessorId` does NOT by itself guarantee a
     * representor — `lessors.rep_name` is a nullable column, so a lessor
     * created before it was required (or edited directly) can still be
     * blank. When the caller has this data (PropertyNew fetches the full
     * lessor list), the representor check uses the resolved name instead of
     * just the id's presence. Omit it (e.g. in tests) to fall back to
     * trusting `lessorId` presence, since most callers won't have the list
     * loaded.
     */
    lessorRepNames?: Record<string, string>;
    /**
     * Grandfather bypass for the owner 15/15 + smoke gate. Set when editing a
     * submission that predates the checklist (zero `apartment_checklist_items`
     * rows on read-back — see useSubmissionForEdit): those owners never had a
     * checklist to fill, so re-gating them would trap their resubmit. New
     * submissions always have the full checklist, so this stays false for them.
     * Mirrors the server's legacy-skip in owner_resubmit_property (COL-529).
     */
    skipChecklistGate?: boolean;
  } = {}
): {
  canAdvance: boolean;
  message?: string;
  /** Non-blocking advisory shown even when `canAdvance` is true (admin only —
   *  the checklist is informational for staff, gating for owners). */
  info?: string;
  /** How many checklist proofs are still open across all apartments, when the
   *  owner gate is what blocks advancing — surfaced next to `message` so the
   *  owner knows how many remain rather than just "something is missing". */
  openCount?: number;
} {
  const { ownerSubmitting = false, lessorRepNames, skipChecklistGate } = opts;

  /** Does this apartment resolve *some* representor, override or inherited? */
  function hasRepresentor(a: WizardState["apartments"][number]): boolean {
    if (a.repName.trim()) return true;
    if (!a.lessorId) return false;
    if (!lessorRepNames) return true; // caller has no lessor data — trust the link
    return !!lessorRepNames[a.lessorId]?.trim();
  }

  /**
   * The owner checklist gate, shared by step 3 (advance) and step 4 (submit):
   * every apartment needs all 15 proofs done and no hard blocker. Same message
   * wording for both steps so one set of i18n cases covers them
   * (translateValidationMessage in PropertyNew). Returns null when the gate
   * passes or is grandfathered away (pre-checklist submission being edited).
   */
  function ownerChecklistGate(): {
    canAdvance: false;
    message: string;
    openCount?: number;
  } | null {
    const progress = state.apartments.map((a) =>
      computeChecklistProgress(a.checklist, {
        bedrooms: bedroomsOf(a.numRooms),
        keynestAvailable: state.property.keynestAvailable,
      })
    );
    // An explicit smoke "no" is a legally-required hard blocker and is never
    // waived — the grandfather exemption only waives *missing* proofs, so this
    // check runs even when the completeness gate is skipped.
    if (progress.some((p) => p.blocked)) {
      return {
        canAdvance: false,
        message:
          'Every apartment needs a smoke detector — resolve the "no" answer before continuing.',
      };
    }
    if (skipChecklistGate) return null;
    if (progress.some((p) => p.done < p.total)) {
      return {
        canAdvance: false,
        message:
          "Every apartment must complete all 15 checklist proofs before you can continue.",
        openCount: progress.reduce(
          (n, p) => n + Math.max(0, p.total - p.done),
          0
        ),
      };
    }
    return null;
  }

  if (step === 1) {
    const ok = !!(
      state.property.title.trim() &&
      state.property.street_name.trim() &&
      state.property.street_number.trim() &&
      state.property.city.trim() &&
      state.property.zip_code.trim()
    );
    if (!ok) {
      return {
        canAdvance: false,
        message: t(
          "admin.v2.propertyNew.step1RequiredFields",
          "Title, street name, street number, postal code, and city are required."
        ),
      };
    }
    // The street name is printed verbatim on the contract and the
    // Wohnungsgeberbestätigung, so an abbreviation can't be carried forward.
    const streetNameError = validateStreetName(state.property.street_name);
    if (streetNameError) {
      return { canAdvance: false, message: streetNameError };
    }
    return { canAdvance: true };
  }
  if (step === 2) {
    if (state.apartments.length === 0) {
      return {
        canAdvance: false,
        message: t(
          "admin.v2.propertyNew.addAtLeastOneApartment",
          "Add at least one apartment."
        ),
      };
    }
    if (state.apartments.some((a) => !a.label.trim())) {
      return {
        canAdvance: false,
        message: t(
          "admin.v2.propertyNew.everyApartmentNeedsLabel",
          "Every apartment needs a label."
        ),
      };
    }
    if (state.apartments.some((a) => a.rooms.some((r) => !r.title.trim()))) {
      return {
        canAdvance: false,
        message: t(
          "admin.v2.propertyNew.everyRoomNeedsName",
          "Every room needs a name."
        ),
      };
    }
    // A representor is required on every contract ("vertreten durch"); an
    // apartment resolves one either from its own override (repName) or from
    // its linked owner's default (Lessor.rep_name) — see hasRepresentor.
    if (!ownerSubmitting && state.apartments.some((a) => !hasRepresentor(a))) {
      return {
        canAdvance: false,
        message: t(
          "admin.v2.propertyNew.everyApartmentNeedsRepresentor",
          "Every apartment needs a representor."
        ),
      };
    }
    return { canAdvance: true };
  }
  if (step === 3) {
    // Step 3 — Checkliste (COL-529). Role decides whether the 15 proofs gate.
    if (!ownerSubmitting) {
      // Staff/admin: the checklist is informational — advancing is always
      // allowed. A "no" smoke-detector answer is surfaced as a non-blocking
      // warning so staff know go-live is blocked until the owner resolves it.
      const blocked = state.apartments.some(
        (a) =>
          computeChecklistProgress(a.checklist, {
            bedrooms: bedroomsOf(a.numRooms),
            keynestAvailable: state.property.keynestAvailable,
          }).blocked
      );
      if (blocked) {
        return {
          canAdvance: true,
          info: 'At least one apartment has its smoke-detector check marked "no" — go-live is blocked until that\'s resolved.',
        };
      }
      return { canAdvance: true };
    }
    // Owner submitting their own building: every apartment must have all 15
    // proofs done and no hard blocker before they can continue.
    return ownerChecklistGate() ?? { canAdvance: true };
  }
  // Step 4 — final submit. Earlier checks must hold.
  if (
    !state.property.title.trim() ||
    !state.property.street_name.trim() ||
    !state.property.street_number.trim() ||
    validateStreetName(state.property.street_name) ||
    !state.property.city.trim() ||
    !state.property.zip_code.trim() ||
    state.apartments.length === 0 ||
    (!ownerSubmitting && state.apartments.some((a) => !hasRepresentor(a)))
  ) {
    return {
      canAdvance: false,
      message: t(
        "admin.v2.propertyNew.someRequiredFieldsMissing",
        "Some required fields are missing — use the Edit links above."
      ),
    };
  }
  // Re-assert the step-3 owner gate at final submit: the step is skippable by
  // jumping straight here, so the 15/15-and-no-blocker rule has to hold again
  // where the write actually happens, not only on the step-3 → step-4 advance.
  if (ownerSubmitting) {
    const gate = ownerChecklistGate();
    if (gate) return gate;
  }
  return { canAdvance: true };
}
